import { useMemo, useRef, useState } from "react";
import type { SeriesPoint } from "@gazelab/analysis";

/** Colores de grupo del panel (A = azul, B = ámbar); validados para daltonismo como par categórico. */
export const GROUP_COLORS = { A: "#0369a1", B: "#d97706" } as const;

export interface TimelineSeries {
  key: "A" | "B";
  label: string;
  points: SeriesPoint[];
}

const W = 1000;
const H = 220;
const M = { top: 12, right: 16, bottom: 30, left: 64 };
const COV_H = 50;

const fmtTime = (ms: number) => {
  const s = ms / 1000;
  return s < 60 ? `${Number(s.toFixed(1)).toLocaleString("es-MX")} s` : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

/**
 * Curvas por ventana de tiempo para los dos grupos (media + banda de IC 95 %), con cabezal de
 * reproducción, intervalo resaltado, retícula al pasar el puntero y clic para saltar a ese momento.
 * Debajo, en un gráfico aparte (nunca un segundo eje), los participantes con datos por ventana.
 */
export function VideoTimeline({
  durationMs,
  series,
  coverage,
  timeMs,
  yLabel,
  yMax,
  formatY,
  highlight,
  onSeek,
  testId,
}: {
  durationMs: number;
  series: TimelineSeries[];
  /** Participantes con datos por ventana, por grupo (mismo orden que `series`). */
  coverage: Array<{ key: "A" | "B"; values: number[] }>;
  timeMs: number;
  yLabel: string;
  yMax: number;
  formatY: (v: number) => string;
  /** Intervalo resaltado (p. ej. el del ROI elegido). */
  highlight?: { startMs: number | null; endMs: number | null } | null;
  onSeek: (ms: number) => void;
  testId?: string;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const plotW = W - M.left - M.right;
  const plotH = H - M.top - M.bottom;
  const x = (ms: number) => M.left + (Math.min(Math.max(ms, 0), durationMs) / Math.max(1, durationMs)) * plotW;
  const y = (v: number) => M.top + plotH - (Math.min(v, yMax) / yMax) * plotH;
  const windows = series[0]?.points ?? [];
  const mid = (p: SeriesPoint) => (p.startMs + p.endMs) / 2;

  const paths = useMemo(
    () =>
      series.map((s) => {
        let line = "";
        let started = false;
        const segments: string[] = [];
        let upper: string[] = [];
        let lower: string[] = [];
        const flush = () => {
          if (upper.length > 1) segments.push(`M${upper.join("L")}L${lower.reverse().join("L")}Z`);
          upper = [];
          lower = [];
        };
        for (const p of s.points) {
          if (p.mean === null) {
            started = false;
            flush();
            continue;
          }
          line += `${started ? "L" : "M"}${x(mid(p)).toFixed(1)},${y(p.mean).toFixed(1)}`;
          started = true;
          if (p.ciLow !== null && p.ciHigh !== null) {
            upper.push(`${x(mid(p)).toFixed(1)},${y(p.ciHigh).toFixed(1)}`);
            lower.push(`${x(mid(p)).toFixed(1)},${y(p.ciLow).toFixed(1)}`);
          } else flush();
        }
        flush();
        return { key: s.key, line, band: segments.join("") };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, durationMs, yMax],
  );

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * yMax);
  const step = durationMs > 120_000 ? 30_000 : durationMs > 40_000 ? 10_000 : durationMs > 15_000 ? 5_000 : 2_000;
  const xTicks: number[] = [];
  for (let t = 0; t <= durationMs; t += step) xTicks.push(t);

  function msAt(e: { clientX: number }) {
    const r = svgRef.current!.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    return Math.min(durationMs, Math.max(0, ((px - M.left) / plotW) * durationMs));
  }
  const hoverIdx = hover === null ? null : windows.findIndex((w) => hover >= w.startMs && hover < w.endMs);
  const covMax = Math.max(1, ...coverage.flatMap((c) => c.values));

  return (
    <div className="relative" data-testid={testId}>
      <div className="mb-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600" aria-label="Leyenda">
        {series.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5">
            <span className="inline-block h-0.5 w-4 rounded" style={{ background: GROUP_COLORS[s.key] }} />
            <span className="font-medium text-slate-700">{s.key}:</span> {s.label}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-4 rounded-sm bg-slate-300/60" /> IC 95 %
        </span>
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H + COV_H}`}
        className="h-auto w-full cursor-crosshair select-none"
        role="img"
        aria-label={`${yLabel} por ventana de tiempo, grupos A y B`}
        onPointerMove={(e) => setHover(msAt(e))}
        onPointerLeave={() => setHover(null)}
        onClick={(e) => onSeek(msAt(e))}
      >
        {highlight && (highlight.startMs !== null || highlight.endMs !== null) && (
          <rect
            x={x(highlight.startMs ?? 0)}
            y={M.top}
            width={Math.max(0, x(highlight.endMs ?? durationMs) - x(highlight.startMs ?? 0))}
            height={plotH}
            className="fill-sky-100/60"
          />
        )}
        {yTicks.map((t) => (
          <g key={t}>
            <line x1={M.left} x2={W - M.right} y1={y(t)} y2={y(t)} className="stroke-slate-200" strokeWidth={1} />
            <text x={M.left - 6} y={y(t)} dy="0.32em" textAnchor="end" className="fill-slate-500 text-[15px]">
              {formatY(t)}
            </text>
          </g>
        ))}
        {xTicks.map((t) => (
          <text key={t} x={x(t)} y={H - 6} textAnchor="middle" className="fill-slate-500 text-[15px]">
            {fmtTime(t)}
          </text>
        ))}
        <text x={14} y={M.top + plotH / 2} transform={`rotate(-90 14 ${M.top + plotH / 2})`} textAnchor="middle" className="fill-slate-500 text-[15px]">
          {yLabel}
        </text>
        {paths.map((p) => (
          <path key={`band-${p.key}`} d={p.band} fill={GROUP_COLORS[p.key]} fillOpacity={0.14} stroke="none" />
        ))}
        {paths.map((p) => (
          <path key={`line-${p.key}`} d={p.line} fill="none" stroke={GROUP_COLORS[p.key]} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        ))}
        {/* Participantes con datos por ventana (gráfico aparte, misma escala de tiempo). */}
        <text x={M.left - 6} y={H + 10} textAnchor="end" className="fill-slate-500 text-[14px]">
          n
        </text>
        {coverage.map((c, ci) =>
          c.values.map((v, i) => {
            const w = windows[i];
            if (!w) return null;
            const bw = Math.max(1, (x(w.endMs) - x(w.startMs)) / coverage.length - 1);
            const bh = (v / covMax) * (COV_H - 14);
            return <rect key={`${c.key}-${i}`} x={x(w.startMs) + ci * (bw + 1)} y={H + COV_H - 4 - bh} width={bw} height={bh} rx={1} fill={GROUP_COLORS[c.key]} fillOpacity={0.55} />;
          }),
        )}
        <line x1={x(timeMs)} x2={x(timeMs)} y1={M.top} y2={H + COV_H - 4} className="stroke-ink" strokeWidth={1.5} />
        {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={M.top} y2={H + COV_H - 4} className="stroke-slate-400" strokeDasharray="3 3" strokeWidth={1} />}
      </svg>
      {hoverIdx !== null && hoverIdx >= 0 && (
        <div
          className="pointer-events-none absolute top-6 z-10 min-w-44 rounded-lg bg-white px-3 py-2 text-xs shadow-lg ring-1 ring-slate-200"
          style={{ left: `min(calc(${(x(hover!) / W) * 100}% + 12px), calc(100% - 12rem))` }}
          role="status"
        >
          <p className="mb-1 font-util text-slate-500">
            {fmtTime(windows[hoverIdx]!.startMs)} – {fmtTime(windows[hoverIdx]!.endMs)}
          </p>
          {series.map((s, si) => {
            const p = s.points[hoverIdx];
            return (
              <p key={s.key} className="flex items-center gap-2">
                <span className="inline-block h-0.5 w-3 rounded" style={{ background: GROUP_COLORS[s.key] }} />
                <strong className="tabular-nums text-ink">{p?.mean == null ? "—" : formatY(p.mean)}</strong>
                <span className="text-slate-500">
                  {s.key} · n = {coverage[si]?.values[hoverIdx] ?? p?.n ?? 0}
                </span>
              </p>
            );
          })}
          <p className="mt-1 text-slate-400">Clic para ir a este momento</p>
        </div>
      )}
    </div>
  );
}
